/**
 * AUDIO module — owns all Web Audio synthesis: rain bed + vinyl crackle,
 * walking jazz bass, footsteps, clue/dial/resolution SFX, monologue murmur.
 * No AudioContext exists before title:dismissed (autoplay policy); every
 * other handler no-ops until the engine is live. monologue:done is emitted
 * exactly once per monologue:play, after beat.durationMs, even if synthesis
 * is unavailable. Owned by AUDIO; see CONTRACTS.md.
 */
import type { ModuleContext, ZoneId } from '../core/types'
import { createEngine } from './engine'
import type { AudioEngine } from './engine'
import { createFootstepTracker } from './footsteps'
import { startJazz } from './jazz'
import { playMurmur } from './monologue'
import { startAmbientBed } from './rain'
import type { AmbientBed } from './rain'
import { playCluePickup, playFootstep, playPhoneDial, playResolution } from './sfx'

export const initAudio = (ctx: ModuleContext): void => {
  const { bus } = ctx

  let engine: AudioEngine | null = null
  let bed: AmbientBed | null = null
  let zone: ZoneId = 'outdoor'

  // Browsers require a user gesture before audio playback; the title card
  // dismissal is that gesture. Build the whole graph lazily here.
  bus.on('title:dismissed', () => {
    if (engine) return
    engine = createEngine()
    void engine.ctx.resume()
    bed = startAmbientBed(engine)
    bed.setZone(zone)
    startJazz(engine)
  })

  bus.on('zone:changed', ({ zone: nextZone }) => {
    zone = nextZone
    bed?.setZone(nextZone)
  })

  const onStride = createFootstepTracker(() => {
    if (engine) playFootstep(engine, zone)
  })
  bus.on('player:moved', ({ position }) => {
    onStride(position)
  })

  bus.on('clue:collected', () => {
    if (engine) playCluePickup(engine)
  })

  bus.on('game:accuse', () => {
    if (engine) playPhoneDial(engine)
  })

  bus.on('game:won', () => {
    if (engine) playResolution(engine, true)
  })

  bus.on('game:lost', () => {
    if (engine) playResolution(engine, false)
  })

  // Contract: exactly one monologue:done per monologue:play, after
  // ~durationMs, regardless of whether the murmur synthesis could run.
  bus.on('monologue:play', ({ beat }) => {
    if (engine) playMurmur(engine, beat.durationMs)
    window.setTimeout(() => {
      bus.emit('monologue:done', { beatId: beat.id })
    }, beat.durationMs)
  })
}
